import { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  machineService,
  foamTypeService,
  productionRunService,
} from '../../services/foam/foamServices';
import { NumberField } from '../common';
import { useNotifications } from '../../hooks/useNotifications';
import { apiErrorMessage } from '../../lib/apiErrorMessage';
import type { ProductionRunDto } from '../../types/foam';
import { en } from '../../locale';

interface StartNewRunForm {
  machineId: number | '';
  foamTypeId: number | '';
  gradeRef: string;
  runNo: number | '';
}

interface StartNewRunDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (run: ProductionRunDto) => void;
}

const DEFAULT_VALUES: StartNewRunForm = { machineId: '', foamTypeId: '', gradeRef: '', runNo: '' };

export default function StartNewRunDialog({ open, onClose, onCreated }: StartNewRunDialogProps) {
  const queryClient = useQueryClient();
  const { success, error: notifyError } = useNotifications();

  const { data: machines = [] } = useQuery({
    queryKey: ['machines'],
    queryFn: () => machineService.getAll(),
    enabled: open,
  });
  const { data: foamTypes = [] } = useQuery({
    queryKey: ['foamTypes'],
    queryFn: () => foamTypeService.getAll(),
    enabled: open,
  });

  const { control, handleSubmit, reset } = useForm<StartNewRunForm>({
    defaultValues: DEFAULT_VALUES,
  });

  const createMutation = useMutation({
    mutationFn: (form: StartNewRunForm) =>
      productionRunService.create({
        machineId: form.machineId === '' ? undefined : Number(form.machineId),
        foamTypeId: form.foamTypeId === '' ? undefined : Number(form.foamTypeId),
        gradeRef: form.gradeRef.trim() || undefined,
        runNo: form.runNo === '' ? undefined : Number(form.runNo),
        runDate: new Date().toISOString(),
      }),
    onSuccess: (run) => {
      void queryClient.invalidateQueries({ queryKey: ['productionRuns'] });
      success(en.runs.createSuccess);
      reset(DEFAULT_VALUES);
      onCreated(run);
    },
    onError: (err) => notifyError(apiErrorMessage(err, en.runs.createFailed)),
  });

  const handleClose = () => {
    if (createMutation.isPending) return;
    reset(DEFAULT_VALUES);
    onClose();
  };

  const fieldSx = useMemo(() => ({ mt: 1 }), []);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>{en.startNewRun.title}</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          id="start-run-form"
          onSubmit={handleSubmit((form) => createMutation.mutate(form))}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
        >
          <Controller
            name="machineId"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                select
                label="Machine"
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error ? en.startNewRun.machineRequired : ''}
                sx={fieldSx}
              >
                {machines.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.name ?? `Machine ${m.id}`}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="foamTypeId"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                select
                label={en.startNewRun.foamType}
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error ? en.startNewRun.typeRequired : ''}
              >
                {foamTypes.map((ft) => (
                  <MenuItem key={ft.id} value={ft.id}>
                    {ft.name ?? `Type ${ft.id}`}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="gradeRef"
            control={control}
            render={({ field }) => <TextField {...field} label="Grade" fullWidth />}
          />
          <Controller
            name="runNo"
            control={control}
            render={({ field }) => (
              <NumberField
                label={en.startNewRun.runNo}
                fullWidth
                name={field.name}
                onBlur={field.onBlur}
                inputRef={field.ref}
                value={field.value ?? ''}
                onChange={(v) => field.onChange(v ?? '')}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createMutation.isPending}>
          {en.common.cancel}
        </Button>
        <Button
          type="submit"
          form="start-run-form"
          variant="contained"
          disabled={createMutation.isPending}
        >
          {en.runs.createButton}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
